// =============================================================================
// reverent.tsx — m02 "Yodgorlik" (Memorial · Bukhara · Islamic)
//
// Where m01 "Tinch" practices the discipline of silence, "Yodgorlik" is its
// decorated counterpart. Reverence here is expressed not through subtraction
// but through tradition: the page is wrapped in a girih (knot) tessellation
// that gently breathes behind the hero, three Quranic verses are presented
// in mihrab-style frames, and the family speaks in italic short remembrances.
//
// Cultural notes:
//   * Arabic verses are canonical text, set RTL with generous leading.
//   * No countdown, RSVP, guestbook, or music — these are not appropriate
//     for an Islamic memorial. The visit is the gesture; we don't gamify it.
//   * Sound: silence. We deliberately do not import useSound. No chimes.
//
// Signature element:
//   The girih tessellation behind the hero gently breathes — opacity
//   oscillates 0.10 → 0.14 over 6s ease-in-out infinite. Reduced-motion
//   freezes it at the lower opacity.
// =============================================================================

import { useEffect, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { MapSection, ShareSection, GuestFooter, StorySection } from "@/components/sections";
import * as O from "@/components/Ornaments";
import { useT } from "@/lib/i18n";
import type { VariantProps } from "./types";

// ─── prefers-reduced-motion ─────────────────────────────────────────────

function usePrefersReducedMotion(): boolean {
  const [reduce, setReduce] = useState<boolean>(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e: MediaQueryListEvent) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

// ─── slow, opacity-only reveal ──────────────────────────────────────────

interface QuietRevealProps {
  children: ReactNode;
  delay?: number;
  style?: CSSProperties;
  className?: string;
}

function QuietReveal({ children, delay = 0, style, className }: QuietRevealProps) {
  const reduce = usePrefersReducedMotion();
  const ref = useRef<HTMLDivElement | null>(null);
  const [shown, setShown] = useState<boolean>(reduce);

  useEffect(() => {
    if (reduce) {
      setShown(true);
      return;
    }
    const el = ref.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setShown(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShown(true);
            obs.disconnect();
            break;
          }
        }
      },
      { threshold: 0.08, rootMargin: "0px 0px -40px 0px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [reduce]);

  return (
    <div
      ref={ref}
      className={className}
      style={{
        ...style,
        opacity: shown ? 1 : 0,
        transition: reduce ? "none" : `opacity 1.8s ease ${delay}ms`,
        willChange: reduce ? undefined : "opacity",
      }}
    >
      {children}
    </div>
  );
}

// ─── girih tessellation — full-bleed pattern behind the hero ────────────
//
// The same tile geometry as the layout's preview, scaled larger and tinted
// in antique gold. We use a CSS animation (`yodgorlikBreathe`) on the
// wrapping div so the whole pattern oscillates — opacity-only, no
// transform, no filter shifts.

function GirihField({ accent }: { accent: string }) {
  // Build the 8-point star polygon points once.
  const cx = 50;
  const cy = 50;
  const R = 24;
  const r = R * 0.541;
  const pts: Array<[number, number]> = [];
  for (let i = 0; i < 16; i++) {
    const a = (i * 22.5 - 90) * (Math.PI / 180);
    const radius = i % 2 === 0 ? R : r;
    pts.push([cx + Math.cos(a) * radius, cy + Math.sin(a) * radius]);
  }
  const star = pts.map(([x, y]) => `${x},${y}`).join(" ");
  const innerStar = pts
    .map(([x, y]) => `${cx + (x - cx) * 0.55},${cy + (y - cy) * 0.55}`)
    .join(" ");

  return (
    <svg
      aria-hidden
      width="100%"
      height="100%"
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        display: "block",
      }}
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <pattern
          id="yodgorlik-girih"
          x="0"
          y="0"
          width="120"
          height="120"
          patternUnits="userSpaceOnUse"
        >
          <g stroke={accent} strokeWidth="0.7" fill="none">
            <polygon points={star} />
            <polygon points={innerStar} opacity="0.7" />
            <circle cx={cx} cy={cy} r="1.2" fill={accent} stroke="none" />
            {[
              [0, 0],
              [100, 0],
              [0, 100],
              [100, 100],
            ].map(([x, y], i) => (
              <g key={i} transform={`translate(${x} ${y})`}>
                <polygon points={star} opacity="0.55" />
              </g>
            ))}
            <line x1="50" y1="26" x2="50" y2="0" />
            <line x1="50" y1="74" x2="50" y2="100" />
            <line x1="26" y1="50" x2="0" y2="50" />
            <line x1="74" y1="50" x2="100" y2="50" />
          </g>
        </pattern>
      </defs>
      <rect width="100%" height="100%" fill="url(#yodgorlik-girih)" />
    </svg>
  );
}

// ─── small mihrab arch SVG used to crown each Quranic verse panel ──────

function MihrabArch({ color, width = 72 }: { color: string; width?: number }) {
  const h = width * 0.7;
  return (
    <svg
      aria-hidden
      width={width}
      height={h}
      viewBox="0 0 72 50"
      style={{ display: "block", margin: "0 auto" }}
    >
      <g fill="none" stroke={color} strokeWidth="0.8">
        {/* Pointed (lancet) arch — Bukhara/Timurid silhouette */}
        <path d="M4 50 L 4 26 Q 4 6 36 2 Q 68 6 68 26 L 68 50" />
        <path d="M10 50 L 10 28 Q 10 12 36 9 Q 62 12 62 28 L 62 50" opacity="0.55" />
        {/* Small finial */}
        <line x1="36" y1="0" x2="36" y2="6" />
        <circle cx="36" cy="0" r="1.4" fill={color} stroke="none" />
      </g>
    </svg>
  );
}

// Decorative gold corner — a tight little 8-fold rosette used at each corner
// of a verse mihrab.
function GoldCorner({
  color,
  size = 28,
  rotate = 0,
}: {
  color: string;
  size?: number;
  rotate?: number;
}) {
  return (
    <svg
      aria-hidden
      width={size}
      height={size}
      viewBox="0 0 28 28"
      style={{
        display: "block",
        transform: `rotate(${rotate}deg)`,
      }}
    >
      <g fill="none" stroke={color} strokeWidth="0.8">
        <path d="M0 14 L 14 14 L 14 0" />
        <path d="M2 14 L 14 14 L 14 2" opacity="0.55" />
        <circle cx="14" cy="14" r="1.6" fill={color} stroke="none" />
        {/* tiny 8-point rosette */}
        <g transform="translate(14 14)">
          {Array.from({ length: 8 }).map((_, i) => {
            const a = (i * 45 * Math.PI) / 180;
            const x = Math.cos(a) * 5;
            const y = Math.sin(a) * 5;
            return <line key={i} x1="0" y1="0" x2={x} y2={y} />;
          })}
        </g>
      </g>
    </svg>
  );
}

// ─── Quranic verse panel (mihrab style) ─────────────────────────────────

interface VersePanelProps {
  arabic: string;
  uzbek: string;
  reference: string;
  ink: string;
  accent: string;
  bg: string;
  font: string;
}

function VersePanel({ arabic, uzbek, reference, ink, accent, bg, font }: VersePanelProps) {
  return (
    <div
      className="yod-verse"
      style={{
        position: "relative",
        background: `${bg}`,
        border: `1px solid ${accent}55`,
        padding: "56px 40px 44px",
        textAlign: "center",
        boxShadow: `inset 0 0 0 1px ${bg}, inset 0 0 0 2px ${accent}22`,
      }}
    >
      {/* Four gold corners — small geometric rosettes */}
      <div style={{ position: "absolute", top: 6, left: 6 }}>
        <GoldCorner color={accent} rotate={0} />
      </div>
      <div style={{ position: "absolute", top: 6, right: 6 }}>
        <GoldCorner color={accent} rotate={90} />
      </div>
      <div style={{ position: "absolute", bottom: 6, left: 6 }}>
        <GoldCorner color={accent} rotate={270} />
      </div>
      <div style={{ position: "absolute", bottom: 6, right: 6 }}>
        <GoldCorner color={accent} rotate={180} />
      </div>

      <MihrabArch color={accent} width={68} />

      {/* Arabic — generous leading, RTL */}
      <p
        lang="ar"
        dir="rtl"
        className="yod-verse-arabic"
        style={{
          fontFamily: font,
          fontSize: "clamp(22px, 2.6vw, 30px)",
          lineHeight: 2,
          color: ink,
          margin: "24px 0 0",
          letterSpacing: "0.02em",
        }}
      >
        {arabic}
      </p>

      <div
        aria-hidden
        style={{
          height: 1,
          width: 64,
          background: `${accent}66`,
          margin: "28px auto 24px",
        }}
      />

      {/* Uzbek translation — italic Marcellus */}
      <p
        className="yod-verse-uz"
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: "clamp(15px, 1.6vw, 18px)",
          lineHeight: 1.75,
          color: `${ink}cc`,
          margin: 0,
          maxWidth: 480,
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        {uzbek}
      </p>

      {/* Reference — small caps */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: "0.36em",
          color: accent,
          marginTop: 22,
        }}
      >
        {reference}
      </div>
    </div>
  );
}

// ─── verse data — canonical, well-known text ────────────────────────────

// Arabic stays canonical; uzbek meaning + reference live in the dictionary.
const VERSES_ARABIC: string[] = [
  "إِنَّا لِلَّٰهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ",
  "كُلُّ نَفْسٍ ذَائِقَةُ الْمَوْتِ",
  "يَا أَيَّتُهَا النَّفْسُ الْمُطْمَئِنَّةُ ارْجِعِي إِلَىٰ رَبِّكِ رَاضِيَةً مَّرْضِيَّةً",
];

// ─── family tributes — short remembrances ───────────────────────────────
// Tribute copy lives in the dictionary; we render four entries by index.
const TRIBUTE_COUNT = 4;

// ─── variant ────────────────────────────────────────────────────────────

export function ReverentVariant({ t, isDark }: VariantProps) {
  const tt = useT();
  const reduce = usePrefersReducedMotion();
  const photo = t.photos && t.photos.length ? t.photos[0] : undefined;

  return (
    <>
      <style>{`
        @keyframes yodgorlikBreathe {
          0%, 100% { opacity: 0.10; }
          50%      { opacity: 0.14; }
        }
        @media (prefers-reduced-motion: reduce) {
          .yod-girih-wrap { animation: none !important; opacity: 0.10 !important; }
        }

        .yod-variant a:focus-visible,
        .yod-variant button:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 2px;
        }

        /* ── Mobile lockdown ─────────────────────────────────────────── */
        @media (max-width: 768px) {
          .yod-variant button, .yod-variant a { min-height: 44px; }
          .yod-variant .yod-hero {
            padding: 64px 18px 48px !important;
            min-height: auto !important;
          }
          .yod-variant .yod-hero h1.yod-name {
            font-size: clamp(36px, 12vw, 96px) !important;
          }
          .yod-variant .yod-hero-arabic {
            font-size: 22px !important;
            line-height: 1.9 !important;
          }
          .yod-variant .yod-hero-translit {
            font-size: 13px !important;
          }
          .yod-variant .yod-frame {
            width: 72vw !important;
            margin: 36px auto !important;
          }
          .yod-variant .yod-name {
            font-size: 40px !important;
            letter-spacing: 0.02em !important;
            line-height: 1.15 !important;
          }
          .yod-variant .yod-years {
            font-size: 22px !important;
            letter-spacing: 0.16em !important;
          }
          .yod-variant .yod-marosim {
            padding: 18px 24px !important;
          }
          .yod-variant .yod-marosim-date {
            font-size: 17px !important;
          }
          .yod-variant .yod-uz-meaning {
            font-size: 15px !important;
            line-height: 1.75 !important;
          }
          .yod-variant .yod-verses-section {
            padding: 72px 20px !important;
          }
          .yod-variant .yod-verse {
            padding: 44px 24px 36px !important;
          }
          .yod-variant .yod-verse-arabic {
            font-size: 22px !important;
            line-height: 2.0 !important;
          }
          .yod-variant .yod-verse-uz {
            font-size: 14px !important;
          }
          .yod-variant .yod-tributes-section {
            padding: 72px 24px !important;
          }
          .yod-variant .yod-tribute {
            padding: 28px 22px !important;
          }
          .yod-variant .yod-tribute-text {
            font-size: 16px !important;
            line-height: 1.7 !important;
          }
        }
      `}</style>

      <div
        className="yod-variant"
        style={{ position: "relative", background: t.bg, color: t.ink }}
      >
        {/* ── HERO ───────────────────────────────────────────────────── */}
        <section
          className="yod-hero"
          style={{
            position: "relative",
            minHeight: "100vh",
            padding: "104px 32px",
            display: "grid",
            placeItems: "center",
            textAlign: "center",
            overflow: "hidden",
          }}
        >
          {/* Breathing girih tessellation — full-bleed behind the hero */}
          <div
            className="yod-girih-wrap"
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              opacity: 0.12,
              animation: reduce ? "none" : "yodgorlikBreathe 6s ease-in-out infinite",
              pointerEvents: "none",
            }}
          >
            <GirihField accent={t.accent} />
          </div>

          {/* Hairline frame inside the hero — references the layout card */}
          <div
            aria-hidden
            style={{
              position: "absolute",
              inset: "32px",
              border: `1px solid ${t.accent}33`,
              pointerEvents: "none",
            }}
          />

          <QuietReveal style={{ maxWidth: 640, width: "100%", position: "relative", zIndex: 1 }}>
            {/* Arabic mourning recitation — above name */}
            <p
              lang="ar"
              dir="rtl"
              className="yod-hero-arabic"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(26px, 3.4vw, 36px)",
                lineHeight: 1.9,
                color: t.accent,
                margin: 0,
                letterSpacing: "0.02em",
              }}
            >
              إِنَّا لِلَّٰهِ وَإِنَّا إِلَيْهِ رَاجِعُونَ
            </p>

            <p
              className="yod-hero-translit"
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(14px, 1.4vw, 16px)",
                color: `${t.ink}aa`,
                marginTop: 8,
                marginBottom: 0,
                letterSpacing: "0.04em",
              }}
            >
              {tt("variant.m02.hero.translit")}
            </p>

            {/* Museum-mounted portrait, gold-bordered */}
            <div
              className="yod-frame"
              style={{
                width: "min(300px, 64vw)",
                aspectRatio: "3/4",
                margin: "48px auto 40px",
                border: `1.5px solid ${t.accent}`,
                padding: 14,
                background: t.bg,
                boxShadow: `inset 0 0 0 1px ${t.bg}, inset 0 0 0 2px ${t.accent}55`,
                position: "relative",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  outline: `1px solid ${t.accent}66`,
                  outlineOffset: "-1px",
                  background: `${t.ink}0E`,
                  overflow: "hidden",
                }}
              >
                {photo ? (
                  <div
                    aria-label={`${t.names} fotosurati`}
                    style={{
                      position: "absolute",
                      inset: 0,
                      backgroundImage: `url("${photo}")`,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      filter: "sepia(0.18) saturate(0.85) contrast(0.96)",
                    }}
                  />
                ) : (
                  // Inline candle — neutral, sober placeholder. Sober line
                  // drawing in the layout's ink color, with a soft amber flame
                  // tinted by the gold accent so it sits inside the palette.
                  <svg
                    aria-hidden="true"
                    viewBox="0 0 200 280"
                    style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
                  >
                    <ellipse cx="100" cy="92" rx="7" ry="14" fill={t.accent} opacity="0.55" />
                    <ellipse cx="100" cy="94" rx="3" ry="8" fill="#F4D38A" />
                    <line x1="100" y1="102" x2="100" y2="120" stroke={t.ink} strokeWidth="1.5" opacity="0.7" />
                    <ellipse cx="100" cy="120" rx="16" ry="4" fill="none" stroke={t.ink} strokeWidth="1.4" opacity="0.7" />
                    <line x1="84" y1="120" x2="84" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                    <line x1="116" y1="120" x2="116" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                    <ellipse cx="100" cy="228" rx="28" ry="6" fill="none" stroke={t.ink} strokeWidth="1.4" opacity="0.7" />
                    <line x1="72" y1="228" x2="128" y2="228" stroke={t.ink} strokeWidth="1.6" opacity="0.7" />
                  </svg>
                )}
              </div>

              {/* Diamond-shaped gold pins at each mat corner */}
              {[
                ["top", "left"],
                ["top", "right"],
                ["bottom", "left"],
                ["bottom", "right"],
              ].map(([v, h], i) => (
                <div
                  key={i}
                  style={{
                    position: "absolute",
                    width: 8,
                    height: 8,
                    background: t.accent,
                    [v]: -4,
                    [h]: -4,
                    transform: "rotate(45deg)",
                  } as CSSProperties}
                />
              ))}
            </div>

            <h1
              className="yod-name"
              style={{
                fontFamily: t.font,
                fontSize: "clamp(40px, 6vw, 64px)",
                fontWeight: 400,
                margin: 0,
                letterSpacing: "0.02em",
                lineHeight: 1.1,
              }}
            >
              {t.names}
            </h1>

            {/* Years line */}
            <div style={{ marginTop: 16, marginBottom: 36 }}>
              <div
                className="yod-years"
                style={{
                  fontFamily: t.font,
                  fontStyle: "italic",
                  fontSize: 26,
                  color: `${t.ink}cc`,
                  letterSpacing: "0.18em",
                  fontVariantNumeric: "lining-nums",
                }}
              >
                1942{" — "}2026
              </div>
              <div
                aria-hidden
                style={{
                  height: 1,
                  width: 64,
                  background: `${t.accent}88`,
                  margin: "16px auto 0",
                }}
              />
            </div>

            {/* Uzbek meaning of the mourning verse — beneath name+years */}
            <p
              className="yod-uz-meaning"
              style={{
                fontFamily: "var(--font-body)",
                fontStyle: "italic",
                fontSize: 18,
                lineHeight: 1.8,
                color: `${t.ink}cc`,
                margin: "0 auto 36px",
                maxWidth: 520,
              }}
            >
              {tt("variant.m02.hero.uz_meaning")}
            </p>

            <div
              className="yod-marosim"
              style={{
                padding: "22px 36px",
                border: `1px solid ${t.accent}66`,
                display: "inline-block",
                background: `${t.bg}`,
              }}
            >
              <div
                style={{
                  fontFamily: "var(--font-ui)",
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: "0.4em",
                  color: t.accent,
                  marginBottom: 8,
                }}
              >
                {tt("variant.m02.hero.marosim_label")}
              </div>
              <div
                className="yod-marosim-date"
                style={{ fontFamily: t.font, fontSize: 22, fontStyle: "italic" }}
              >
                {t.date} · {t.venue}
              </div>
            </div>
          </QuietReveal>
        </section>

        {/* ── STORY ─────────────────────────────────────────────────── */}
        <QuietReveal>
          <StorySection
            t={t}
            eyebrow={tt("variant.m02.story.eyebrow")}
            title={tt("variant.m02.story.title")}
            body={tt("variant.m02.story.body")}
          />
        </QuietReveal>

        {/* ── QURANIC VERSES ───────────────────────────────────────── */}
        <section
          className="yod-verses-section"
          style={{
            padding: "120px 32px",
            borderTop: `1px solid ${t.ink}22`,
            background: t.bg,
          }}
        >
          <QuietReveal style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5em",
                color: t.accent,
                marginBottom: 18,
              }}
            >
              {tt("variant.m02.verses.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(32px, 4vw, 44px)",
                fontWeight: 400,
                margin: 0,
                color: t.ink,
                letterSpacing: "-0.005em",
              }}
            >
              {tt("variant.m02.verses.title")}
            </h2>
            <div
              aria-hidden
              style={{
                height: 1,
                width: 72,
                background: `${t.accent}66`,
                margin: "24px auto 0",
              }}
            />
          </QuietReveal>

          <div
            style={{
              maxWidth: 760,
              margin: "64px auto 0",
              display: "grid",
              gap: 36,
            }}
          >
            {VERSES_ARABIC.map((arabic, i) => (
              <QuietReveal key={i} delay={i * 80}>
                <VersePanel
                  arabic={arabic}
                  uzbek={tt(`variant.m02.verses.${i}.uzbek`)}
                  reference={tt(`variant.m02.verses.${i}.reference`)}
                  ink={t.ink}
                  accent={t.accent}
                  bg={t.bg}
                  font={t.font}
                />
              </QuietReveal>
            ))}
          </div>
        </section>

        {/* ── FAMILY TRIBUTES ──────────────────────────────────────── */}
        <section
          className="yod-tributes-section"
          style={{
            padding: "120px 32px",
            borderTop: `1px solid ${t.ink}22`,
            background: t.bg,
          }}
        >
          <QuietReveal style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
            <div
              style={{
                fontFamily: "var(--font-ui)",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.5em",
                color: t.accent,
                marginBottom: 18,
              }}
            >
              {tt("variant.m02.tributes.eyebrow")}
            </div>
            <h2
              style={{
                fontFamily: t.font,
                fontStyle: "italic",
                fontSize: "clamp(32px, 4vw, 44px)",
                fontWeight: 400,
                margin: 0,
                color: t.ink,
                letterSpacing: "-0.005em",
              }}
            >
              {tt("variant.m02.tributes.title")}
            </h2>
            <div
              aria-hidden
              style={{
                height: 1,
                width: 72,
                background: `${t.accent}66`,
                margin: "24px auto 0",
              }}
            />
          </QuietReveal>

          <div
            style={{
              maxWidth: 760,
              margin: "56px auto 0",
              display: "grid",
              gap: 28,
              gridTemplateColumns: "1fr",
            }}
          >
            {Array.from({ length: TRIBUTE_COUNT }).map((_, i) => (
              <QuietReveal key={i} delay={i * 60}>
                <div
                  className="yod-tribute"
                  style={{
                    padding: "32px 36px",
                    border: `1px solid ${t.accent}44`,
                    background: t.bg,
                    position: "relative",
                  }}
                >
                  {/* tiny corner ornament — single rosette at top-left */}
                  <div
                    aria-hidden
                    style={{ position: "absolute", top: 8, left: 8, opacity: 0.7 }}
                  >
                    <O.Diamond size={10} color={t.accent} />
                  </div>
                  <div
                    aria-hidden
                    style={{ position: "absolute", top: 8, right: 8, opacity: 0.7 }}
                  >
                    <O.Diamond size={10} color={t.accent} />
                  </div>

                  <p
                    className="yod-tribute-text"
                    style={{
                      fontFamily: t.font,
                      fontStyle: "italic",
                      fontSize: 18,
                      lineHeight: 1.75,
                      color: t.ink,
                      margin: 0,
                    }}
                  >
                    "{tt(`variant.m02.tributes.${i}.text`)}"
                  </p>
                  <div
                    aria-hidden
                    style={{
                      height: 1,
                      width: 32,
                      background: `${t.accent}77`,
                      margin: "20px 0 14px",
                    }}
                  />
                  <div
                    style={{
                      fontFamily: "var(--font-ui)",
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: "0.36em",
                      color: t.accent,
                    }}
                  >
                    {tt(`variant.m02.tributes.${i}.relation`).toUpperCase()}
                  </div>
                  <div
                    style={{
                      fontFamily: t.font,
                      fontSize: 16,
                      marginTop: 4,
                      color: `${t.ink}dd`,
                    }}
                  >
                    {tt(`variant.m02.tributes.${i}.name`)}
                  </div>
                </div>
              </QuietReveal>
            ))}
          </div>
        </section>

        <QuietReveal>
          <MapSection
            t={t}
            isDark={isDark}
            addressLine={`${t.venue} · ${tt("variant.m02.map.address_suffix")}`}
          />
        </QuietReveal>

        <QuietReveal>
          <ShareSection t={t} />
        </QuietReveal>

        <GuestFooter t={t} />
      </div>
    </>
  );
}
